import React from 'react';
import { FeatherAlertTriangle } from '@subframe/core';
import { detectMultiCollectionInTrade, type MultiCollectionResult } from '../../utils/tradeWarnings';

interface MultiCollectionWarningProps {
  sideA: { collection_id?: string | null; collection_name?: string | null }[];
  sideB: { collection_id?: string | null; collection_name?: string | null }[];
  compact?: boolean;
  className?: string;
}

function getMessage(result: MultiCollectionResult, totalItems: number): string | null {
  const { hasMultipleCollections, hasUnknownCollection } = result;

  if (hasMultipleCollections && hasUnknownCollection) {
    return 'This trade contains NFTs from multiple collections, including unrecognized ones. Verify each NFT is from the collection you expect before proceeding.';
  }
  if (hasMultipleCollections) {
    return 'This trade contains NFTs from multiple collections. Verify each NFT is from the collection you expect before proceeding.';
  }
  // Only warn about unknown collections when there are 2+ NFTs.
  // A single NFT from an unknown collection isn't a spoofing risk —
  // it's just an un-indexed collection.
  if (hasUnknownCollection && totalItems >= 2) {
    return 'This trade includes NFTs from an unrecognized collection. Verify each NFT before proceeding.';
  }
  return null;
}

export default function MultiCollectionWarning({ sideA, sideB, compact, className }: MultiCollectionWarningProps) {
  const result = detectMultiCollectionInTrade(sideA, sideB);
  const message = getMessage(result, sideA.length + sideB.length);
  if (!message) return null;

  return (
    <div className={`flex items-start gap-2 text-amber-600 bg-amber-950/30 border border-amber-800/50 rounded-md ${compact ? 'px-3 py-2' : 'px-4 py-3'} ${className || ''}`}>
      <FeatherAlertTriangle className={`flex-shrink-0 ${compact ? 'h-4 w-4 mt-0.5' : 'h-5 w-5 mt-0.5'}`} />
      <span className={compact ? 'text-xs' : 'text-sm'}>{message}</span>
    </div>
  );
}
